'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, t } from '@lingui/macro';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  Typography,
} from '@mui/material';
import { Button, ToggleButtonGroupInput } from '@prismo-io/design-system';
import { type FC, useEffect, useMemo, useTransition } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useSWRConfig } from 'swr';
import { useProgress } from '../../../components/progress';
import { useSendSkill } from '../../../hooks/use-send-skill';
import { useUpdateSkill } from '../../../hooks/use-update-skill';
import type { SkillFromWorkerT } from '../../../utils/fetch-skills-from-worker';
import {
  SkillSchema,
  type SkillSchemaNullableT,
  type SkillSchemaT,
} from './schema';

type SkillFormProps = {
  defaultValues?: Partial<SkillSchemaT>;
  onComplete?: SubmitHandler<SkillSchemaT>;
  onCompleteUpdate?: SubmitHandler<SkillSchemaT>;
  skill: SkillFromWorkerT;
  skillId?: string;
  currentSkillId: number;
};

export const SkillForm: FC<SkillFormProps> = (props) => {
  const {
    defaultValues = {},
    onComplete = undefined,
    onCompleteUpdate = undefined,
    skill,
    skillId = null,
    currentSkillId,
  } = props;

  const { setCurrent } = useProgress(currentSkillId + 1);

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
    setValue,
    watch,
    reset,
  } = useForm<SkillSchemaNullableT>({
    mode: 'all',
    resolver: zodResolver(SkillSchema),
    defaultValues: {
      skill,
      level: null,
      interest: null,
      ...defaultValues,
    },
  });

  const [isTransiting, startTransition] = useTransition();

  const isLoading = useMemo(
    () => isTransiting || isSubmitting,
    [isTransiting, isSubmitting]
  );

  useEffect(() => {
    setValue('skill', skill);
  }, [skill]);

  const { trigger: sendSkill } = useSendSkill();
  const { trigger: sendUpdateSkill } = useUpdateSkill();

  const valuesWatched = watch();

  const { mutate } = useSWRConfig();

  // SUBMIT
  const onSubmit: SubmitHandler<SkillSchemaNullableT> = (data) => {
    const validData = SkillSchema.parse(data);

    const { skill, level, interest } = validData;

    if (skillId) {
      return sendUpdateSkill(
        {
          id: skillId,
          type: skill.type,
          level: level && level.level ? level.level : null,
          interest: interest && interest.interest ? interest.interest : null,
        },
        {
          onSuccess: async () => {
            startTransition(() => {
              mutate('skill-quark-sidebar');

              if (onCompleteUpdate) {
                onCompleteUpdate(validData);
              }
            });
          },
        }
      );
    }

    return sendSkill(
      { skill, level, interest },
      {
        onSuccess: async () => {
          startTransition(() => {
            reset();
            mutate('skill-quark-sidebar');

            setCurrent(currentSkillId + 1);
            if (onComplete) {
              onComplete(validData);
            }
          });
        },
      }
    );
  };

  useEffect(() => {
    if (
      typeof valuesWatched.level?.hasLevel === 'boolean' &&
      !valuesWatched.level.hasLevel
    ) {
      setValue('level.level', null);
    }
    if (
      typeof valuesWatched.interest?.hasInterest === 'boolean' &&
      !valuesWatched.interest.hasInterest
    ) {
      setValue('interest.interest', null);
    }
  }, [valuesWatched.level?.hasLevel, valuesWatched.interest?.hasInterest]);

  const withInterest = useMemo(() => {
    if (
      typeof valuesWatched.level === 'undefined' ||
      valuesWatched.level === null
    ) {
      return false;
    }

    if (valuesWatched.level.hasLevel && valuesWatched.level.level) return true;
    if (valuesWatched.level.hasLevel === false) return true;

    return false;
  }, [valuesWatched.level?.hasLevel, valuesWatched.level?.level]);

  const withNextSkill = useMemo(() => {
    if (
      typeof valuesWatched.level === 'undefined' ||
      typeof valuesWatched.interest === 'undefined' ||
      valuesWatched.level === null ||
      valuesWatched.interest === null
    ) {
      return false;
    }

    // OUI OUI
    if (
      valuesWatched.level.hasLevel === false &&
      valuesWatched.interest.hasInterest === false
    ) {
      return true;
    }
    // OUI NON
    if (valuesWatched.level.hasLevel) {
      if (
        valuesWatched.level.level &&
        valuesWatched.interest.hasInterest === false
      ) {
        return true;
      }
    }
    // NON OUI
    if (valuesWatched.interest.hasInterest) {
      if (
        valuesWatched.interest.interest &&
        valuesWatched.level.hasLevel === false
      ) {
        return true;
      }
    }
    // NON NON
    if (
      valuesWatched.level.hasLevel &&
      valuesWatched.interest.hasInterest &&
      valuesWatched.level.level &&
      valuesWatched.interest.interest
    ) {
      return true;
    }
    return false;
  }, [
    valuesWatched.level?.hasLevel,
    valuesWatched.level?.level,
    valuesWatched.interest?.hasInterest,
    valuesWatched.interest?.interest,
  ]);

  return (
    <Stack
      spacing={{ none: 2, md: 5 }}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack textAlign="center" gap={{ none: 1, md: 2 }} marginX={3}>
        <Typography variant="h2">{skill?.name}</Typography>
      </Stack>

      {/* LEVEL */}
      <Stack spacing={{ none: 1, md: 3 }}>
        <Stack alignItems="center" gap={{ none: 1, md: 2 }}>
          <Trans>Avez-vous cette compétence ?</Trans>
          <ToggleButtonGroupInput
            control={control}
            name="level.hasLevel"
            orientation="horizontal"
            color="primary"
            exclusive
            disabled={isLoading}
            enforceAtLeastOneSelected
          >
            <ToggleButton value={true} aria-label="true">
              <Trans>Oui</Trans>
            </ToggleButton>
            <ToggleButton value={false} aria-label="false">
              <Trans>Non</Trans>
            </ToggleButton>
          </ToggleButtonGroupInput>
        </Stack>
        {valuesWatched?.level?.hasLevel && (
          <Stack
            direction="column"
            alignItems="center"
            gap={{ none: 1, md: 2 }}
          >
            <Trans>Quel est votre niveau ?</Trans>
            <Controller
              name={'level.level'}
              control={control}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { error },
              }) => {
                return (
                  <RadioGroup
                    sx={{ display: 'flex', flexDirection: 'row' }}
                    onChange={(event, value) => {
                      onChange(value);
                    }}
                    onBlur={(event) => {
                      onBlur();
                    }}
                    value={value}
                  >
                    <FormControlLabel
                      value={1}
                      control={<Radio />}
                      label={t`Débutant`}
                      labelPlacement="bottom"
                      disabled={isLoading}
                    />
                    <FormControlLabel
                      value={2}
                      control={<Radio />}
                      label={t`Intermédiaire`}
                      labelPlacement="bottom"
                      disabled={isLoading}
                    />
                    <FormControlLabel
                      value={3}
                      control={<Radio />}
                      label={t`Expert`}
                      labelPlacement="bottom"
                      disabled={isLoading}
                    />
                  </RadioGroup>
                );
              }}
            />
          </Stack>
        )}
      </Stack>

      {/* INTEREST */}
      {withInterest && (
        <Stack spacing={{ none: 1, md: 3 }}>
          <Stack alignItems="center" gap={{ none: 1, md: 2 }}>
            <Trans>Êtes-vous intéressé par cette compétence ?</Trans>
            <ToggleButtonGroupInput
              control={control}
              name="interest.hasInterest"
              orientation="horizontal"
              color="primary"
              exclusive
              disabled={isLoading}
              enforceAtLeastOneSelected
            >
              <ToggleButton value={true} aria-label="true">
                <Trans>Oui</Trans>
              </ToggleButton>
              <ToggleButton value={false} aria-label="false">
                <Trans>Non</Trans>
              </ToggleButton>
            </ToggleButtonGroupInput>
          </Stack>
          {valuesWatched?.interest?.hasInterest && (
            <Stack
              direction="column"
              alignItems="center"
              gap={{ none: 1, md: 2 }}
            >
              <Trans>Quel est votre niveau d'intérêt ?</Trans>
              <Controller
                name={'interest.interest'}
                control={control}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { error },
                }) => {
                  return (
                    <RadioGroup
                      sx={{ display: 'flex', flexDirection: 'row' }}
                      onChange={(event, value) => {
                        onChange(value);
                      }}
                      onBlur={(event) => {
                        onBlur();
                      }}
                      value={value}
                    >
                      <FormControlLabel
                        value={1}
                        control={<Radio />}
                        label={t`Faible`}
                        labelPlacement="bottom"
                        disabled={isLoading}
                      />
                      <FormControlLabel
                        value={2}
                        control={<Radio />}
                        label={t`Moyen`}
                        labelPlacement="bottom"
                        disabled={isLoading}
                      />

                      <FormControlLabel
                        value={3}
                        control={<Radio />}
                        label={t`Fort`}
                        labelPlacement="bottom"
                        disabled={isLoading}
                      />
                    </RadioGroup>
                  );
                }}
              />
            </Stack>
          )}
        </Stack>
      )}

      {/* BUTTON */}
      {withNextSkill && (
        <Stack direction="row" justifyContent="center">
          <Button
            variant="contained"
            color="gradient"
            type="submit"
            disabled={!withNextSkill || isLoading}
          >
            {!skillId && <Trans>Compétences suivantes {'>'}</Trans>}
            {skillId && <Trans>Mettre à jour</Trans>}
          </Button>
        </Stack>
      )}

      {isLoading && (
        <Box
          component="div"
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgcolor="background.paper"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ opacity: 0.5 }}
        >
          <CircularProgress />
        </Box>
      )}
    </Stack>
  );
};
